import React from "react";
import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Tooltip,
  styled,
  InputBase,
  Box,
  IconButton,
} from "@mui/material";
import {
  Search,
  ChevronLeft,
  NavigateNext,
  Verified,
  Phone,
} from "@mui/icons-material";

import { Colors } from "../../config/default";
import MuiModels from "../models";
import { formatDollarAmount, formatValue } from "../../common";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getTruncatedText } from "../../common";
import ScrollbarStyles from "./../customScroll";
import PaynoteButton from "../payments/seamlessChexPaynote";
import {
  creditorBusinessDetails,
  creditorPeronsalDetails,
} from "../../constants/appConstants";
import DialPad from "../dialPad";
import { useDispatch } from "react-redux";
import { setDialState } from "../../redux/action/action";

const SearchContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: Colors.BG_LIGHT_GRAY,
  "&:hover": {
    backgroundColor: Colors.BG_LIGHT_GRAY,
  },
  fontSize: "10px",
  width: "40%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    fontSize: "10px",
    paddingLeft: `calc(1em + ${theme.spacing(2.5)})`,
    transition: theme.transitions.create("width"),
  },
}));

export default function CreditorsDetailCards({
  accountsExist,
  caseData,
  GetCaseDetails,
  GetLogsById,
  verifiedSenders,
  fetchCalls,
  getAllRanges,
  cc,
  getAttorneyData,
}) {
  const [searchText, setSearchText] = useState("");
  const [startIndex, setStartIndex] = useState(0);
  const dispatch = useDispatch();

  const itemsPerPage = 2;

  const handleNext = () => {
    if (startIndex + itemsPerPage < caseData?.creditor?.contacts?.length) {
      setStartIndex(startIndex + itemsPerPage);
    }
  };
  const handlePrev = () => {
    if (startIndex - itemsPerPage >= 0) {
      setStartIndex(startIndex - itemsPerPage);
    }
  };

  const smallScreen = useMediaQuery("(min-width:315px) and (max-width:760px)");

  const griRelationdStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  const gridActionStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  };
  const cellStyle = {
    display: "flex",
    alignItems: "center",
    color: Colors.DIM_LIGHT_GRAY,
    fontWeight: "600",
    fontFamily: "Nunito",
    fontSize: "11px",
  };
  const iconStyle = {
    fontSize: "13px",
    marginLeft: ".3rem",
    marginTop: ".3rem",
  };
  const filteredContacts = caseData?.creditor?.contacts?.filter((item) =>
    item?.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  useEffect(() => {
    setStartIndex(0);
  }, [searchText]);

  const paginatedContacts = filteredContacts?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleDialPad = (phoneNumber, caseId, fetchCalls) => {
    dispatch(
      setDialState({
        phoneNumber: phoneNumber,
        caseId: caseId,
        fetchCalls: fetchCalls,
        isModalOpen: true,
      })
    );
  };

  return (
    <>
      <Grid
        item
        xs={12}
        lg={3.8}
        sx={{
          backgroundColor: Colors.WHITE,
          borderRadius: "10px",
          padding: "0px 10px",
          height: "14rem",
          marginBottom: "0.5rem",
        }}
      >
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p
              style={{
                fontWeight: "600",
                fontSize: "13px",
                fontFamily: "Nunito",
              }}
            >
              {creditorPeronsalDetails}
            </p>
            <div
              style={{
                display: "flex",
                alignContent: "center",
                justifyContent: "space-between",
              }}
            >
              <MuiModels
                type="case"
                show="showCreditorSync"
                iconColor={Colors.BLACK}
                caseData={caseData}
                GetCaseDetails={GetCaseDetails}
                height="22vh"
              />
              <MuiModels
                show="creditorDetail"
                button="create"
                iconColor={Colors.BLACK}
                width="80vw"
                height="75vh"
                caseData={caseData}
                GetCaseDetails={GetCaseDetails}
                getAllRanges={getAllRanges}
                getAttorneyData={getAttorneyData}
              />
            </div>
          </div>
          {[
            {
              label: "Full Name",
              value: caseData?.creditor?.basicInformation?.fullName,
            },
            {
              label: "Email",
              value: caseData?.creditor?.basicInformation?.email,
              showVerifiedIcon: caseData?.creditor?.paynoteUserFound,
            },
            {
              label: "Phone #",
              value: caseData?.creditor?.basicInformation?.phone,
            },
          ]?.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: smallScreen ? "11px" : "13px",
                  fontFamily: "Nunito",
                  color: Colors.DARK_GRAY,
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {item?.label || "--"}
                {item?.label === "Email" && item?.showVerifiedIcon && (
                  <Tooltip
                    placement="top"
                    title={
                      caseData?.paynoteUserFound
                        ? "Verified with Paynote"
                        : "--"
                    }
                  >
                    <Verified
                      sx={{
                        fontSize: "16px",
                        color: "green",
                        marginLeft: "4px",
                      }}
                    />
                  </Tooltip>
                )}
              </Typography>
              {item?.label === "Phone #" ? (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    sx={{ color: Colors.SKY_BLUE }}
                    onClick={() =>
                      handleDialPad(
                        item?.value ? `1${item?.value}` : "",
                        caseData?._id,
                        fetchCalls
                      )
                    }
                  >
                    <Phone sx={{ fontSize: "16px" }} />
                  </IconButton>
                  <Tooltip title={item?.value || "--"} placement="top-end">
                    <Typography
                      sx={{
                        fontSize: smallScreen ? "11px" : "13px",
                        color: Colors.DIM_LIGHT_GRAY,
                        fontFamily: "Nunito",
                        fontWeight: "500",
                        textAlign: "right",
                      }}
                    >
                      {getTruncatedText(formatValue(item?.value), 15) || "--"}
                    </Typography>
                  </Tooltip>
                </div>
              ) : (
                <Tooltip title={item?.value || ""} placement="top-end">
                  <Typography
                    sx={{
                      fontSize: smallScreen ? "11px" : "13px",
                      color: Colors.DIM_LIGHT_GRAY,
                      fontFamily: "Nunito",
                      fontWeight: "500",
                      textAlign: "right",
                    }}
                  >
                    {getTruncatedText(formatValue(item?.value), 15) || "--"}
                  </Typography>
                </Tooltip>
              )}
            </div>
          ))}
        </>

        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MuiModels
            accountsExist={accountsExist}
            type="case"
            show="paynoteForm"
            button="paynoteForm"
            width="55vw"
            caseData={caseData}
          />
        </span>
      </Grid>
      <Grid
        item
        xs={12}
        lg={4}
        sx={{
          backgroundColor: Colors.WHITE,
          borderRadius: "10px",
          padding: "0px 10px",
          height: "14rem",
          marginBottom: ".5rem",
        }}
      >
        <p
          style={{
            fontWeight: "600",
            fontSize: "13px",
            fontFamily: "Nunito",
          }}
        >
          {creditorBusinessDetails}
        </p>
        {[
          {
            label: "Company",
            value: caseData?.creditor?.businessInformation?.companyName || "--",
          },
          {
            label: "Debt Type",
            value:
              caseData?.creditor?.businessInformation?.businessCategory || "--",
          },
          {
            label: "Name in Bank Account",
            value: caseData?.creditor?.accountTitle || "--",
          },
        ]?.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: smallScreen ? "11px" : "13px",
                fontFamily: "Nunito",
                fontWeight: "700",
                color: Colors.DARK_GRAY,
                width: "45%",
              }}
            >
              {item?.label}
            </Typography>
            <Tooltip title={item?.value || ""} placement="top-end">
              <Typography
                sx={{
                  fontSize: smallScreen ? "11px" : "13px",
                  color: Colors.DIM_LIGHT_GRAY,
                  fontFamily: "Nunito",
                  fontWeight: "500",
                  textAlign: "right",
                  wordBreak: "break-word",
                }}
              >
                {getTruncatedText(item?.value, 15)}
              </Typography>
            </Tooltip>
          </div>
        ))}
      </Grid>

      <Grid
        item
        xs={12}
        lg={4}
        sx={{
          backgroundColor: Colors.WHITE,
          borderRadius: "10px",
          padding: "0px 10px",
          height: "14rem",
          marginBottom: "0.5rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ paddingTop: "0.5rem" }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              fontFamily: "Nunito",
            }}
          >
            Contacts
          </Typography>
          <MuiModels
            show="creditorContacts"
            caseData={caseData}
            GetCaseDetails={GetCaseDetails}
            width="70vw"
          />
        </Grid>
        <Grid container sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
          <SearchContainer
            sx={{
              width: "100%",
              marginBottom: smallScreen ? "0.5rem" : "0rem",
            }}
          >
            <SearchIconWrapper>
              <Search
                sx={{
                  fontSize: "16px",
                  color: Colors.DIM_LIGHT_GRAY,
                }}
              />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search Contact..."
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </SearchContainer>
        </Grid>
        <Box>
          <Grid
            container
            sx={{
              backgroundColor: Colors.SKY_BLUE,
              color: Colors.WHITE,
              borderRadius: ".4rem",
              height: "2rem",
              padding: "0 0.4rem",
              alignItems: "center",
            }}
          >
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "11px" }}>Name</Typography>
            </Grid>
            <Grid item xs={4} sx={griRelationdStyle}>
              <Typography sx={{ fontSize: "11px", marginRight: "1rem" }}>
                Phone
              </Typography>
            </Grid>
            <Grid item xs={4} sx={gridActionStyle}>
              <Typography sx={{ fontSize: "11px" }}>Action</Typography>
            </Grid>
          </Grid>
          <Box
            sx={{
              height: "5rem",
              overflow: "auto",
              ...ScrollbarStyles,
            }}
          >
            {paginatedContacts && paginatedContacts.length > 0 ? (
              paginatedContacts?.map((item, index) => (
                <Grid
                  item
                  xs={12}
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    backgroundColor:
                      index % 2 === 0
                        ? Colors.WHITE
                        : "rgba(85, 148, 242, 0.06)",
                    "&:hover": {
                      backgroundColor: Colors.BG_LIGHT_GRAY,
                    },
                    cursor: "pointer",
                    paddingRight: ".4rem",
                    paddingLeft: ".4rem",
                    height: "2rem",
                    alignItems: "center",
                  }}
                >
                  <Grid item xs={4}>
                    <span style={cellStyle}>{item?.name || "-"}</span>
                  </Grid>
                  <Grid item xs={4} sx={griRelationdStyle}>
                    <span style={cellStyle}>{item?.phone || "-"}</span>
                  </Grid>
                  <Grid item xs={4} sx={gridActionStyle}>
                    <span style={cellStyle}>
                      <MuiModels
                        show="sendEmailCase"
                        buttonName="sendEmail"
                        iconColor={Colors.BLACK}
                        maxHeight="78vh"
                        GetLogsById={GetLogsById}
                        caseDataId={caseData?._id}
                        data={caseData}
                        verifiedSenders={verifiedSenders}
                        cc={cc}
                      />
                      <MuiModels
                        show="sendEmailCase"
                        buttonName="sendSms"
                        headerName={true}
                        iconColor={Colors.BLACK}
                        maxHeight="78vh"
                        caseDataId={caseData?._id}
                        GetLogsById={GetLogsById}
                        data={caseData}
                      />
                      <MuiModels
                        show="editCreditorContacts"
                        caseData={caseData}
                        item={item}
                        GetCaseDetails={GetCaseDetails}
                        width="70vw"
                      />
                    </span>
                  </Grid>
                </Grid>
              ))
            ) : (
              <Typography
                sx={{
                  textAlign: "center",
                  padding: "1rem",
                  color: Colors.GREY,
                  fontFamily: "Nunito",
                  fontSize: ".8rem",
                }}
              >
                No Creditor Contacts Exist
              </Typography>
            )}

            {filteredContacts?.length > itemsPerPage && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  position: "absolute",
                  right: "10px",
                }}
              >
                <IconButton
                  aria-label="prev"
                  disabled={startIndex - itemsPerPage < 0}
                  onClick={handlePrev}
                  color="primary"
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  aria-label="next"
                  disabled={
                    startIndex + itemsPerPage >= filteredContacts?.length
                  }
                  onClick={handleNext}
                  color="primary"
                >
                  <NavigateNext />
                </IconButton>
              </div>
            )}
          </Box>
        </Box>
      </Grid>
    </>
  );
}
